import { type FC, useState } from "react"
import { Link } from "react-router"
import styled from "styled-components"

import AspectRatio from "@forento/shared/components/AspectRatio"
import { exhaustiveGuard } from "@forento/shared/utilities/switch"

import { type Downloadable, useDownloadables } from "~/api/downloadable"
import Button from "~/themes/school/components/Button"
import Card, { CardText, CardTitle, Cards } from "~/themes/school/components/Card"
import { TitleSearch } from "~/themes/school/components/ContentSearch"
import Layout from "~/themes/school/components/Layout"
import PageHeader, { PageTitle } from "~/themes/school/components/PageHeader"
import PartialLoadingPage from "~/themes/school/components/PartialLoadingPage"
import { backgroundColor, fonts, lightTextColor } from "~/themes/school/styles"
import { useTranslation } from "~/translations"
import routes from "~/utilities/routes"

const DownloadablesPage: FC = () => {
	const t = useTranslation()
	const downloadables = useDownloadables()

	const [selectedTag, setSelectedTag] = useState<number>()
	const [searchItems, setSearchItems] = useState<number[]>()

	return (
		<Layout>
			<PageHeader>
				<PageTitle>{t("downloadable.title")}</PageTitle>
				<TitleSearch
					items={downloadables.status === "success" ? downloadables.data : []}
					onItemsToShowChange={setSearchItems}
				/>
			</PageHeader>
			{downloadables.status === "error" ? (
				<p>{t("failedToLoad")}</p>
			) : downloadables.status === "loading" ? (
				<PartialLoadingPage />
			) : downloadables.status === "success" ? (
				<>
					{downloadables.tags.length > 0 && (
						<Tags>
							{downloadables.tags.map(tag => (
								<Tag
									key={tag.id}
									$isActive={selectedTag === tag.id}
									onClick={() => setSelectedTag(current => (current !== tag.id ? tag.id : undefined))}
								>
									{tag.label}
								</Tag>
							))}
							{selectedTag !== undefined && (
								<Tag $isActive onClick={() => setSelectedTag(undefined)}>
									{t("clearFilter")}
								</Tag>
							)}
						</Tags>
					)}
					<Downloadables
						downloadables={downloadables.data}
						selectedTag={selectedTag}
						searchItems={searchItems}
					/>
				</>
			) : (
				exhaustiveGuard(downloadables.status)
			)}
		</Layout>
	)
}

type DownloadablesProps = {
	downloadables: Downloadable[]
	selectedTag: number | undefined
	searchItems: number[] | undefined
}
const Downloadables: FC<DownloadablesProps> = ({ downloadables, selectedTag, searchItems }) => {
	let filteredDownloadables = downloadables
	if (selectedTag !== undefined) {
		filteredDownloadables = filteredDownloadables.filter(downloadable => downloadable.tagIds.includes(selectedTag))
	}
	if (searchItems !== undefined) {
		filteredDownloadables = filteredDownloadables
			.filter(downloadable => searchItems.includes(downloadable.id))
			.sort((a, b) => (searchItems ? searchItems.indexOf(b.id) - searchItems.indexOf(a.id) : 0))
	}
	return (
		<Cards>
			{filteredDownloadables.map(downloadable => (
				<Card key={downloadable.id} as={Link} to={routes.downloadable.detail(downloadable.id)}>
					<CardTitle>{downloadable.title}</CardTitle>
					<AspectRatioContainer>
						<StyledAspectRatio aspectRatio={16 / 9} width={{ value: 100, unit: "percent" }}>
							<ThumbnailContainer>
								{downloadable.thumbnailFilePath !== null && (
									<Thumbnail src={downloadable.thumbnailFilePath} />
								)}
								<Price>{downloadable.priceLabel.value}</Price>
							</ThumbnailContainer>
						</StyledAspectRatio>
					</AspectRatioContainer>
					{downloadable.shortDescription && <CardText>{downloadable.shortDescription}</CardText>}
				</Card>
			))}
		</Cards>
	)
}

const Tags = styled.div`
	width: 100%;
	display: flex;
	gap: 22px;
	flex-wrap: wrap;
	margin-bottom: 24px;
`

const Tag = styled(Button)<{ $isActive: boolean }>`
	font-family: ${fonts.quicksand};
	font-size: 14px;
	font-weight: bold;
	color: ${props => (props.$isActive ? props.theme.primaryColor : lightTextColor)};
`

const AspectRatioContainer = styled.div`
	margin: 0 42px;
`

const StyledAspectRatio = styled(AspectRatio)`
	background-color: ${backgroundColor};
	overflow: hidden;
`

const ThumbnailContainer = styled.div`
	position: relative;
`

const Thumbnail = styled.img.attrs({ alt: "Downloadable thumbnail" })`
	width: 100%;
	height: 100%;
	object-fit: cover;
`

const Price = styled.p`
	position: absolute;
	top: 16px;
	right: 16px;
	font-weight: bold;
	font-size: 16px;
	color: #ffa412;
	background-color: white;
	padding: 4px 8px;
`

export default DownloadablesPage
